// ── batchReceipt — one receipt for a cascade mark, instead of a toast parade ──
// Marking several episodes at once ("mark watched through here") fires, from one
// tap: the Undo bar, the multi-episode rate sheet, the streak toast, sometimes an
// achievement medallion, a wrap cinematic, a Final Cut card, the push ask. The
// bottom rail serializes them so they no longer overlap, but the informational
// ones still expire on a clock while you read, and the interactive ones arrive in
// rail order, not in the order you'd answer them (Jason 2026-09-22: "give the user
// more time to read them and dismiss or interact with them").
//
// This module is the collector. A cascade (`nextep:progress-marked` with count>1)
// opens a capture session; each consumer that would have shown its own surface
// asks `capture(kind, event)` first and stands down when it returns true. After
// CAPTURE_MS the session decides: THRESHOLD or more items → the BatchReceipt sheet
// presents them all, and keeps capturing anything that lands late (a card mint is
// an async insert) until it closes. Fewer → every captured event is REPLAYED with
// a `_replay` marker, so the consumers show exactly what they would have. A single
// mark never opens a session, so nothing about the one-episode path changes.
//
// Observe-only kinds (wrap) are listed on the receipt but never swallowed — the
// cinematic still plays as itself; the receipt waits behind it.
export const CAPTURE_MS = 1500
export const THRESHOLD = 3

let session = null   // { items:[], decided:boolean, showing:boolean, timer }
const subs = new Set()
const notify = () => subs.forEach(fn => { try { fn(session) } catch { /* one bad sub never breaks the rest */ } })

export function subscribeReceipt(fn) { subs.add(fn); return () => subs.delete(fn) }
export function currentReceipt() { return session }
export function receiptShowing() { return !!(session && session.showing) }

// Called by UndoToast on a cascade mark. Opens the session and captures the undo
// itself as the first item. Returns true when captured (the bar stands down).
export function beginCapture(detail) {
  if (!detail || !(detail.count > 1) || detail._replay) return false
  endSession()
  session = { items: [], decided: false, showing: false, timer: null, mark: detail }
  session.items.push({ kind: 'undo', detail, key: 'undo' })
  session.timer = setTimeout(decide, CAPTURE_MS)
  notify()
  return true
}

// Ask to swallow an event. `key` de-dupes the same fact arriving twice.
export function capture(kind, event, key) {
  const detail = event?.detail
  if (!session || detail?._replay) return false
  const k = key || `${kind}:${detail?.id || detail?.label || detail?.title || detail?.show_id || ''}`
  if (session.items.some(i => i.key === k)) return true
  session.items.push({ kind, detail, key: k })
  notify()
  return true
}

// Note a fact without swallowing its event (the wrap cinematic still plays).
export function observe(kind, detail, key) {
  if (!session) return
  const k = key || `${kind}:${detail?.season || ''}`
  if (session.items.some(i => i.key === k)) return
  session.items.push({ kind, detail, key: k, observed: true })
  notify()
}

function decide() {
  if (!session || session.decided) return
  session.decided = true
  session.timer = null
  const swallowed = session.items.filter(i => !i.observed).length
  if (session.items.length >= THRESHOLD && swallowed >= 2) {
    session.showing = true
    notify()
  } else {
    replay()
  }
}

// Re-dispatch everything we held, in arrival order, marked so nobody captures it again.
function replay() {
  const items = session ? session.items.filter(i => !i.observed) : []
  session = null
  notify()
  if (typeof window === 'undefined') return
  for (const it of items) {
    const name = EVENT_FOR[it.kind]
    if (!name) continue
    try { window.dispatchEvent(new CustomEvent(name, { detail: { ...(it.detail || {}), _replay: true } })) } catch { /* noop */ }
  }
}

const EVENT_FOR = {
  undo: 'nextep:progress-marked',
  rate: 'nextep:episode-watched',
  achievement: 'nextep:achievement-unlocked',
  streak: 'nextep:streak-milestone',
  milestone: 'nextep:milestone',
  keepsake: 'nextep:keepsake-minted',
}

// The receipt closed (Done, swipe, Undo). Late arrivals after this go to their own
// consumers again.
export function endSession() {
  if (!session) return
  clearTimeout(session.timer)
  session = null
  notify()
}

// Test seam.
export function __resetReceipt() { if (session) clearTimeout(session.timer); session = null; subs.clear() }
export function __decideNow() { decide() }
